import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot, Eye, Play, RefreshCw, Rocket, Search, ShieldAlert, Square, Zap,
} from "lucide-react";
import * as api from "../../api";
import { PaConfirmModal, PaDetailsModal } from "./ProviderAutomationModals";
import { PaStatusBadge, paDate, paMoney } from "./providerAutomationStatus";
import { autoReconcileLabel } from "../../lib/autoReconcile";

const NON_EXECUTABLE = new Set(["EXECUTING", "RECONCILING", "CONFIRMED", "BLOCKED", "SIMULATED", "MANUAL_REVIEW", "UNKNOWN", "FAILED", "NOT_ELIGIBLE", "PROMO_DETECTED", "PROMO_CHECK_UNKNOWN", "CANCELLED_MANUAL"]);

const BREAKER_META = {
  NORMAL: { cls: "text-emerald-300", label: "🟢 NORMAL" },
  WARNING: { cls: "text-orange-300", label: "🟠 WARNING" },
  OPEN: { cls: "text-red-400", label: "🔴 OPEN" },
};

const DEFAULT_FREE_FIRE_ROUTES = {
  primary: "free_fire_id",
  secondary: "free_fire_20",
  enabled: { free_fire_id: true, free_fire_20: false },
  mode: "MANUAL",
};

const FREE_FIRE_ROUTES = [
  { id: "free_fire_id", label: "Free Fire ID", subtitle: "Ruta principal actual" },
  { id: "free_fire_20", label: "Free Fire 20%", subtitle: "Ruta secundaria" },
];

const priceCheckedAt = (value) => {
  if (!value) return "Sin consulta";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Sin consulta" : date.toLocaleString("es-VE");
};

const Row = ({ op, actions }) => (
  <tr className="border-b border-[var(--ag7-border-soft)] hover:bg-ag7-surface-2/50"
    data-testid={`pa-row-${op.operation_id}`}>
    <td className="px-3 py-2 font-mono text-xs text-white">
      {op.order_number ? `#${String(op.order_number).padStart(4, "0")}` : op.order_id}
      <span className="block text-[10px] text-zinc-500">{op.order_id}</span>
    </td>
    <td className="px-3 py-2 text-xs text-zinc-300">{op.user_email || "—"}</td>
    <td className="px-3 py-2 text-xs text-zinc-300">{op.game_name}</td>
    <td className="px-3 py-2 text-xs text-white">
      {op.commercial_product}
      <span className="block text-[10px] font-mono text-ag7-cyan">
        {(op.provider_plan?.steps || []).map((s) => s.provider_package).join(" + ") || op.provider_product_id}
        {op.provider_plan?.kind === "COMPOSITE" ? ` (×${op.provider_plan?.total_children})` : ""}
      </span>
    </td>
    <td className="px-3 py-2 font-mono text-xs text-zinc-300">{op.player_id_masked}</td>
    <td className="px-3 py-2 font-mono text-xs text-emerald-300 tabular-nums">{paMoney(op.estimated_cost)}</td>
    <td className="px-3 py-2 text-[11px] text-zinc-400">{paDate(op.created_at)}</td>
    <td className="px-3 py-2">
      <PaStatusBadge status={op.status} autoReconcile={op.auto_reconcile} />
      {op.status === "UNKNOWN" && op.auto_reconcile && (
        <span className="block mt-1 text-[10px] font-mono text-orange-200"
          data-testid={`pa-auto-reconcile-${op.operation_id}`}>
          {autoReconcileLabel(op.auto_reconcile)}
        </span>
      )}
    </td>
    <td className="px-3 py-2">{actions}</td>
  </tr>
);

const Metric = ({ label, value, cls, testid }) => (
  <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">{label}</p>
    <p className={`text-lg font-black font-mono tabular-nums ${cls}`}>{value}</p>
  </div>
);

export const ProviderAutomationPanel = ({ showToast }) => {
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmOp, setConfirmOp] = useState(null);
  const [detailsOp, setDetailsOp] = useState(null);
  const [historyStatus, setHistoryStatus] = useState("");
  const [notices, setNotices] = useState([]);
  const [search, setSearch] = useState("");
  const [policies, setPolicies] = useState([]);
  const [shadow, setShadow] = useState(null);
  const [freeFireRoutes, setFreeFireRoutes] = useState(DEFAULT_FREE_FIRE_ROUTES);
  const [nicknameProvider, setNicknameProvider] = useState("freefirecommunity");
  const [savingFreeFireRoutes, setSavingFreeFireRoutes] = useState(false);
  const [freeFirePrices, setFreeFirePrices] = useState(null);
  const [loadingFreeFirePrices, setLoadingFreeFirePrices] = useState(false);
  const [freeFirePriceAdjustments, setFreeFirePriceAdjustments] = useState({ routes: {} });
  const [applyingPriceSurface, setApplyingPriceSurface] = useState("");
  const freeFireRouteVersion = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    const routeVersionAtRequest = freeFireRouteVersion.current;
    try {
      const [st, open, hist, notif, pol, routeConfig, priceAdjustments, nicknameConfig] = await Promise.all([
        api.paStatus(),
        api.paOperations({ status: "READY_FOR_PROVIDER" }),
        api.paOperations({ scope: "history", status: historyStatus, search }),
        api.paNotifications(),
        api.paPolicies(),
        api.paFreeFireRoutes(),
        api.paFreeFirePriceAdjustments(),
        api.paFreeFireNicknameProvider(),
      ]);
      setStatus(st);
      setPending(open.operations || []);
      setHistory(hist.operations || []);
      setNotices(notif.notifications || []);
      setPolicies(pol.policies || []);
      if (routeVersionAtRequest === freeFireRouteVersion.current) {
        setFreeFireRoutes(routeConfig.free_fire_routes || DEFAULT_FREE_FIRE_ROUTES);
      }
      setFreeFirePriceAdjustments(priceAdjustments.free_fire_price_adjustments || { routes: {} });
      setNicknameProvider(nicknameConfig.provider || "freefirecommunity");
    } catch (e) {
      showToast?.(e?.message || "No se pudo cargar la automatización", "error");
    } finally {
      setLoading(false);
    }
  }, [historyStatus, search, showToast]);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (mode) => {
    if (mode === status?.mode) return;
    if (mode === "AUTOMATIC" && !window.confirm(
      "MODO AUTOMÁTICO: tras aprobar el pago y si PagoStore no detecta promoción, "
      + "la recarga se enviará al proveedor sin confirmación adicional. ¿Continuar?")) return;
    try {
      setStatus(await api.paSetMode(mode));
      showToast?.(`Modo cambiado a ${mode === "AUTOMATIC" ? "AUTOMÁTICO" : "SUPERVISADO"}`, "success");
    } catch (e) {
      showToast?.(e?.message || "No se pudo cambiar el modo", "error");
    }
  };

  const toggleAutomation = async () => {
    try {
      const st = status?.enabled ? await api.paStop("detenida desde el panel admin") : await api.paStart();
      setStatus(st);
      showToast?.(st.enabled ? "Automatización reactivada" : "Automatización DETENIDA", "success");
    } catch (e) {
      showToast?.(e?.message || "No se pudo cambiar el estado", "error");
    }
  };

  const saveFreeFireRoutes = async (nextSettings) => {
    freeFireRouteVersion.current += 1;
    setSavingFreeFireRoutes(true);
    try {
      const out = await api.paSetFreeFireRoutes(nextSettings);
      setFreeFireRoutes(out.free_fire_routes || nextSettings);
      showToast?.("Configuración de rutas Free Fire guardada", "success");
    } catch (e) {
      showToast?.(e?.message || "No se pudo guardar las rutas Free Fire", "error");
    } finally {
      setSavingFreeFireRoutes(false);
    }
  };

  const choosePrimaryFreeFireRoute = (routeId) => {
    if (routeId === freeFireRoutes.primary) return;
    saveFreeFireRoutes({
      ...freeFireRoutes,
      primary: routeId,
      secondary: routeId === "free_fire_id" ? "free_fire_20" : "free_fire_id",
    });
  };

  const toggleFreeFireRoute = (routeId) => {
    saveFreeFireRoutes({
      ...freeFireRoutes,
      enabled: { ...freeFireRoutes.enabled, [routeId]: !freeFireRoutes.enabled?.[routeId] },
    });
  };

  const toggleFreeFireFallback = () => {
    saveFreeFireRoutes({
      ...freeFireRoutes,
      mode: freeFireRoutes.mode === "FALLBACK" ? "MANUAL" : "FALLBACK",
    });
  };

  const setNicknameVerifier = async (provider) => {
    if (provider === nicknameProvider) return;
    try { const out = await api.paSetFreeFireNicknameProvider(provider); setNicknameProvider(out.provider); showToast?.(`Verificador de nickname: ${out.provider === "freefirecommunity" ? "FreeFireCommunity" : "Smile.one"}`, "success"); }
    catch (e) { showToast?.(e?.message || "No se pudo cambiar el verificador", "error"); }
  };

  const refreshFreeFirePrices = async () => {
    setLoadingFreeFirePrices(true);
    try {
      const result = await api.paRefreshFreeFirePrices();
      setFreeFirePrices(result);
      const unavailable = (result.routes || []).filter((route) => route.status !== "AVAILABLE");
      showToast?.(
        unavailable.length ? "Algunas rutas no están disponibles" : "Precios Free Fire actualizados",
        unavailable.length ? "error" : "success",
      );
    } catch (e) {
      showToast?.(e?.message || "No se pudieron consultar los precios Free Fire", "error");
    } finally {
      setLoadingFreeFirePrices(false);
    }
  };

  const applyFreeFirePriceAdjustment = async (quote) => {
    if (!quote || quote.status !== "AVAILABLE") return;
    setApplyingPriceSurface(quote.surface);
    try {
      const out = await api.paApplyFreeFirePriceAdjustment({
        surface: quote.surface,
        checked_at: quote.checked_at,
        packages: (quote.packages || []).map((pkg) => ({
          package: pkg.package,
          provider_amount: pkg.provider_amount,
          live_price_usd: pkg.live_price_usd,
        })),
      });
      setFreeFirePriceAdjustments((current) => ({
        ...current,
        routes: { ...(current.routes || {}), [quote.surface]: out.adjustment },
      }));
      showToast?.(`Ajuste de ${quote.label || quote.surface} aplicado`, "success");
    } catch (e) {
      showToast?.(e?.message || "No se pudo aplicar el ajuste de precios", "error");
    } finally {
      setApplyingPriceSurface("");
    }
  };

  const runShadowRehearsal = async () => {
    const productCode = window.prompt(
      "Ensayo SHADOW (no compra nada): código del producto",
      "bloodstrike_50_6");
    if (!productCode) return;
    const playerId = window.prompt("UID del jugador para el ensayo", "");
    if (!playerId) return;
    setShadow({ running: true });
    try {
      const out = await api.paShadowRehearsal(productCode.trim(), playerId.trim());
      setShadow({ running: false, result: out.rehearsal });
      showToast?.("Ensayo SHADOW completado: no se envió ninguna compra", "success");
    } catch (e) {
      setShadow({ running: false, error: e?.message || "falló el ensayo" });
      showToast?.(e?.message || "No se pudo ensayar", "error");
    }
  };

  const resetBreaker = async () => {
    try {
      setStatus(await api.paResetBreaker());
      showToast?.("Circuit breaker reseteado", "success");
    } catch (e) {
      showToast?.(e?.message || "No se pudo resetear el breaker", "error");
    }
  };

  const execute = async () => {
    if (!confirmOp) return;
    setBusyId(confirmOp.operation_id);
    try {
      const res = await api.paExecute(confirmOp.operation_id);
      const st = res?.operation?.status;
      showToast?.(
        st === "CONFIRMED" ? "Recarga confirmada por el proveedor"
          : st === "SIMULATED" ? "Simulación DRY-RUN completada (no se envió nada al proveedor)"
            : `Operación en ${st}`,
        st === "CONFIRMED" || st === "SIMULATED" ? "success" : "error",
      );
      setConfirmOp(null);
      await load();
    } catch (e) {
      showToast?.(e?.message || "La ejecución fue rechazada", "error");
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (op) => {
    setBusyId(op.operation_id);
    try {
      const res = await api.paRetry(op.operation_id);
      const st = res?.operation?.status;
      showToast?.(st === "CONFIRMED" ? "Recarga confirmada por el proveedor"
        : `Reintento ejecutado · ${st}`, st === "CONFIRMED" ? "success" : "error");
      await load();
    } catch (e) {
      showToast?.(e?.message || "No se pudo reintentar la operación", "error");
    } finally {
      setBusyId(null);
    }
  };

  const reevaluate = async (op) => {
    setBusyId(op.operation_id);
    try {
      const res = await api.paReevaluate(op.operation_id);
      const eligible = res?.operation?.eligible;
      showToast?.(eligible ? `Reevaluada: ${res.reason || "lista para ejecutar"}`
        : `Sigue manual: ${res.reason || "no automatizable"}`, eligible ? "success" : "error");
      await load();
    } catch (e) {
      showToast?.(e?.message || "No se pudo reevaluar la operación", "error");
    } finally {
      setBusyId(null);
    }
  };

  const openDetails = async (op) => {
    try {
      setDetailsOp(await api.paOperation(op.operation_id));
    } catch {
      setDetailsOp(op);
    }
  };

  const breaker = BREAKER_META[status?.circuit_breaker?.health] || BREAKER_META.NORMAL;
  const today = status?.today || {};

  return (
    <div className="space-y-6" data-testid="pa-panel">
      {/* CABECERA */}
      <div className="bg-[var(--ag7-glass)] backdrop-blur-xl border border-ag7-purple/25 rounded-2xl p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ag7-purple/15 border border-ag7-purple/50 flex items-center justify-center">
              <Bot className="w-5 h-5 text-ag7-purple" />
            </div>
            <div>
              <h3 className="font-heading font-black text-white text-base sm:text-lg uppercase tracking-wide">
                Automatización de Proveedor
              </h3>
              <p className="text-[11px] font-mono text-[var(--ag7-text-muted)] uppercase tracking-widest">
                {status?.game || "Free Fire"} · {status?.provider_label || "Inefable Revendedores"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="pa-refresh"
              className="px-3 py-2 rounded-xl border border-[var(--ag7-border)] text-zinc-300 text-xs font-bold inline-flex items-center gap-2 hover:bg-ag7-surface-2">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
            </button>
            <button onClick={toggleAutomation} data-testid="pa-toggle-automation"
              className={`px-4 py-2 rounded-xl text-xs font-black inline-flex items-center gap-2 ${status?.enabled ? "bg-red-600 hover:bg-red-500 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black"}`}>
              {status?.enabled ? <><Square className="w-3.5 h-3.5" /> Detener automatización</>
                : <><Play className="w-3.5 h-3.5" /> Reactivar automatización</>}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-state">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Estado</p>
            <p className={`text-sm font-black font-mono ${status?.enabled ? "text-emerald-300" : "text-red-400"}`}>
              {status?.enabled ? "🟢 ACTIVA" : "🔴 DETENIDA"}
            </p>
          </div>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-mode">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Modo</p>
            <div className="space-y-0.5">
              <button onClick={() => changeMode("SUPERVISED")} data-testid="pa-mode-supervised"
                className={`block text-xs font-mono ${status?.mode !== "AUTOMATIC" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                {status?.mode !== "AUTOMATIC" ? "◉" : "○"} Supervisado
              </button>
              <button onClick={() => changeMode("AUTOMATIC")} data-testid="pa-mode-automatic"
                className={`block text-xs font-mono ${status?.mode === "AUTOMATIC" ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                {status?.mode === "AUTOMATIC" ? "◉" : "○"} Automático
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-promo-filter">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Filtro previo</p>
            <p className="text-xs font-black font-mono text-white">🔍 PagoStore Promo Check</p>
            <p className="text-[10px] font-mono text-zinc-500">
              {status?.promo_filter?.session_configured
                ? `sesión configurada · modo ${status?.promo_filter?.mode}`
                : `sin sesión · modo ${status?.promo_filter?.mode || "strict"}`}
            </p>
          </div>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-breaker">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Circuit Breaker</p>
            <p className={`text-sm font-black font-mono ${breaker.cls}`}>{breaker.label}</p>
            <button onClick={resetBreaker} data-testid="pa-reset-breaker"
              className="mt-1 text-[10px] font-mono uppercase tracking-wider text-ag7-cyan hover:underline">
              Resetear
            </button>
          </div>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-execution-mode">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Ejecución</p>
            <p className={`text-sm font-black font-mono ${status?.production_live ? "text-red-400" : "text-purple-300"}`}>
              {status?.production_live ? "🔴 PRODUCCIÓN REAL" : "🟣 DRY-RUN"}
            </p>
            <p className="text-[10px] font-mono text-zinc-500">
              {status?.production_live
                ? `modo ${status?.execution_mode} · las compras salen al proveedor`
                : `modo ${status?.execution_mode || "—"} · nada se envía`}
            </p>
          </div>
          <div className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2" data-testid="pa-transport">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">Transporte</p>
            <p className={`text-sm font-black font-mono ${status?.transport_connected ? "text-emerald-300" : "text-red-400"}`}>
              {status?.transport_connected ? "🟢 Conectado" : "🔴 Sin transporte"}
            </p>
            <p className="text-[10px] font-mono text-zinc-500 break-words">
              {status?.transport_connected ? "ProductionSubmitter conectado" : status?.transport_reason || "—"}
            </p>
            <button onClick={runShadowRehearsal} disabled={shadow?.running}
              data-testid="pa-shadow-rehearsal"
              className="mt-1 text-[10px] font-mono uppercase tracking-wider text-ag7-cyan hover:underline disabled:opacity-40">
              {shadow?.running ? "Ensayando…" : "Ensayo SHADOW"}
            </button>
          </div>
        </div>

        <section className="border border-[var(--ag7-border-soft)] bg-ag7-surface/40 p-4 space-y-3"
          data-testid="pa-free-fire-routes">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-xs font-black text-white">Rutas Free Fire</p>
              <p className="text-[10px] font-mono text-zinc-500" data-testid="pa-free-fire-routes-mode">
                Modo configurado: {freeFireRoutes.mode === "FALLBACK" ? "FALLBACK" : "MANUAL"}
              </p>
            </div>
            <button type="button" onClick={toggleFreeFireFallback} disabled={savingFreeFireRoutes}
              data-testid="pa-free-fire-fallback-toggle"
              className={`px-3 py-1.5 text-[10px] font-black font-mono border transition-colors disabled:opacity-40 ${freeFireRoutes.mode === "FALLBACK" ? "border-amber-400/70 text-amber-200 bg-amber-400/10" : "border-[var(--ag7-border)] text-zinc-300 hover:bg-ag7-surface-2"}`}>
              {freeFireRoutes.mode === "FALLBACK" ? "FALLBACK ACTIVADO" : "FALLBACK DESACTIVADO"}
            </button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {FREE_FIRE_ROUTES.map((route) => {
              const isPrimary = freeFireRoutes.primary === route.id;
              const isEnabled = Boolean(freeFireRoutes.enabled?.[route.id]);
              return (
                <div key={route.id} className="border border-[var(--ag7-border-soft)] px-3 py-3 space-y-2"
                  data-testid={`pa-free-fire-route-${route.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black text-white">{route.label}</p>
                      <p className="text-[10px] font-mono text-zinc-500">{route.subtitle}</p>
                    </div>
                    <button type="button" onClick={() => choosePrimaryFreeFireRoute(route.id)}
                      disabled={savingFreeFireRoutes || isPrimary}
                      data-testid={`pa-free-fire-primary-${route.id}`}
                      className={`text-[10px] font-mono uppercase transition-colors disabled:opacity-60 ${isPrimary ? "text-ag7-cyan" : "text-zinc-400 hover:text-white"}`}>
                      {isPrimary ? "● Principal" : "○ Usar principal"}
                    </button>
                  </div>
                  <button type="button" onClick={() => toggleFreeFireRoute(route.id)}
                    disabled={savingFreeFireRoutes}
                    data-testid={`pa-free-fire-enabled-${route.id}`}
                    className={`w-full border px-2 py-1.5 text-[10px] font-black font-mono transition-colors disabled:opacity-40 ${isEnabled ? "border-emerald-400/60 text-emerald-200 bg-emerald-400/10" : "border-zinc-600 text-zinc-400 hover:bg-ag7-surface-2"}`}>
                    {isEnabled ? "ACTIVADA" : "DESACTIVADA"}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] font-mono text-zinc-500" data-testid="pa-free-fire-routes-pending-note">
            La ruta principal se aplica a operaciones nuevas; FALLBACK sigue desactivado en recargas.
          </p>
        </section>

        <section className="border border-[var(--ag7-border-soft)] bg-ag7-surface/40 p-4 space-y-3"
          data-testid="pa-free-fire-live-prices">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-black text-white">Precios en vivo Free Fire</p>
              <p className="text-[10px] font-mono text-zinc-500" data-testid="pa-free-fire-prices-checked-at">
                Última consulta: {priceCheckedAt(freeFirePrices?.checked_at)}
              </p>
            </div>
            <button type="button" onClick={refreshFreeFirePrices} disabled={loadingFreeFirePrices}
              data-testid="pa-free-fire-prices-refresh"
              className="px-3 py-1.5 text-[10px] font-black font-mono border border-ag7-cyan/60 text-ag7-cyan hover:bg-ag7-cyan/10 transition-colors disabled:opacity-40">
              {loadingFreeFirePrices ? "CONSULTANDO…" : "CONSULTAR PRECIOS"}
            </button>
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {FREE_FIRE_ROUTES.map((route) => {
              const quote = (freeFirePrices?.routes || []).find((item) => item.surface === route.id);
              const available = quote?.status === "AVAILABLE";
              const applied = freeFirePriceAdjustments.routes?.[route.id];
              return (
                <div key={route.id} className="border border-[var(--ag7-border-soft)] p-3 space-y-2"
                  data-testid={`pa-free-fire-prices-${route.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-black text-white">{route.label}</p>
                      <p className="text-[10px] font-mono text-zinc-500" data-testid={`pa-free-fire-prices-time-${route.id}`}>
                        {priceCheckedAt(quote?.checked_at)}
                      </p>
                    </div>
                    <span className={`text-[10px] font-black font-mono ${available ? "text-emerald-300" : "text-zinc-400"}`}
                      data-testid={`pa-free-fire-prices-status-${route.id}`}>
                      {available ? "● CONECTADA" : "○ NO DISPONIBLE"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[10px] font-mono text-zinc-500" data-testid={`pa-free-fire-prices-applied-${route.id}`}>
                      {applied?.applied_at ? `Ajuste aplicado: ${priceCheckedAt(applied.applied_at)}` : "Sin ajuste aplicado"}
                    </p>
                    <button type="button" onClick={() => applyFreeFirePriceAdjustment(quote)}
                      disabled={!available || applyingPriceSurface === route.id}
                      data-testid={`pa-free-fire-prices-apply-${route.id}`}
                      className="px-2 py-1 text-[10px] font-black font-mono border border-emerald-400/60 text-emerald-200 hover:bg-emerald-400/10 transition-colors disabled:opacity-40">
                      {applyingPriceSurface === route.id ? "APLICANDO…" : "APLICAR AJUSTE"}
                    </button>
                  </div>
                  {!quote && <p className="text-[11px] font-mono text-zinc-500" data-testid={`pa-free-fire-prices-empty-${route.id}`}>Sin consulta.</p>}
                  {quote?.reason && <p className="text-[10px] font-mono text-amber-200" data-testid={`pa-free-fire-prices-reason-${route.id}`}>{quote.reason}</p>}
                  {(quote?.packages || []).length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-[10px] font-mono" data-testid={`pa-free-fire-prices-table-${route.id}`}>
                        <thead className="text-zinc-500">
                          <tr><th className="pb-1">Paquete</th><th className="pb-1">Monto</th><th className="pb-1 text-right">Precio real</th></tr>
                        </thead>
                        <tbody>
                          {quote.packages.map((pkg) => (
                            <tr key={`${route.id}-${pkg.provider_amount}`} className="border-t border-[var(--ag7-border-soft)]"
                              data-testid={`pa-free-fire-price-${route.id}-${pkg.provider_amount}`}>
                              <td className="py-1 text-zinc-300">{pkg.package}</td>
                              <td className="py-1 text-ag7-cyan">{pkg.provider_amount}</td>
                              <td className={`py-1 text-right ${pkg.state === "AVAILABLE" ? "text-emerald-300" : "text-zinc-500"}`}>
                                {pkg.state === "AVAILABLE" ? paMoney(pkg.live_price_usd) : "No disponible"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {shadow?.result && (
          <div className="rounded-xl border border-purple-500/40 bg-purple-500/5 px-4 py-3 space-y-1"
            data-testid="pa-shadow-result">
            <p className="text-xs font-black font-mono text-purple-200">
              ENSAYO SHADOW · {shadow.result.product_code} · UID {shadow.result.player_id_masked}
            </p>
            <p className="text-[11px] font-mono text-zinc-300">
              Sesión: {shadow.result.session_state?.session_active ? "activa ✅" : "sin sesión ❌"} ·
              {" "}Precio: {shadow.result.price_check?.reason} ·
              {" "}POST enviado: {shadow.result.post_sent ? "SÍ ⚠️" : "NO ✅"}
            </p>
            {(shadow.result.steps || []).filter((s) => s.nonce_fingerprint).slice(0, 1).map((s, i) => (
              <p key={i} className="text-[11px] font-mono text-zinc-400 break-words">
                Formulario {s.endpoint} · nonce {s.nonce_fingerprint} ·
                {" "}campos {(s.payload_fields || []).join(", ")} · {s.reason}
              </p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Metric label="Exitosas hoy" value={today.confirmed ?? 0} cls="text-emerald-300" testid="pa-metric-confirmed" />
          <Metric label="Promos detectadas" value={today.promo_detected ?? 0} cls="text-emerald-200" testid="pa-metric-promo" />
          <Metric label="Manuales" value={(today.manual_not_eligible ?? 0) + (today.promo_unknown ?? 0)} cls="text-zinc-300" testid="pa-metric-manual" />
          <Metric label="Pendientes" value={today.pending ?? 0} cls="text-amber-300" testid="pa-metric-pending" />
          <Metric label="Desconocidas" value={(today.unknown ?? 0) + (today.promo_unknown ?? 0)} cls="text-orange-300" testid="pa-metric-unknown" />
          <Metric label="Fallidas" value={today.failed ?? 0} cls="text-red-400" testid="pa-metric-failed" />
        </div>

        {policies.length > 0 && (
          <div className="space-y-2" data-testid="pa-policies">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)]">
              Juegos automatizados
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {policies.map((p) => (
                <div key={p.game_slug} data-testid={`pa-policy-${p.game_slug}`}
                  className="rounded-xl border border-[var(--ag7-border-soft)] px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-black text-white">
                      {p.icon} {p.game_label}
                    </p>
                    <span className={`text-[11px] font-mono ${p.approval_behavior === "AUTO_ON_PAYMENT_APPROVED" ? "text-emerald-300" : "text-amber-300"}`}>
                      {p.approval_behavior === "AUTO_ON_PAYMENT_APPROVED" ? "🟢" : "🟡"} {p.status_label}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] font-mono text-zinc-400">{p.eligible_note}</p>
                  <p className="text-[10px] font-mono text-zinc-500">
                    {(p.products || []).length} producto(s) con mapping ·{" "}
                    {(p.products || []).filter((x) => x.kind === "COMPOSITE").length} compuesto(s)
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {notices.length > 0 && (
          <div className="space-y-2" data-testid="pa-notifications">
            {notices.slice(0, 5).map((n) => (
              <div key={n.operation_id}
                data-testid={`pa-notification-${n.operation_id}`}
                className={`rounded-xl border px-3 py-2 text-xs font-mono ${n.status === "PROMO_DETECTED" ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-100" : "border-yellow-400/40 bg-yellow-500/10 text-yellow-100"}`}>
                <p className="font-bold">
                  {n.status === "PROMO_DETECTED" ? "🎁 PROMOCIÓN DETECTADA" : "⚠️ NO SE PUDO VERIFICAR LA PROMOCIÓN"}
                  {" · "}Pedido {n.order_number ? `#${String(n.order_number).padStart(4, "0")}` : n.order_id}
                </p>
                <p>Producto: {n.commercial_product} · UID: {n.player_id_masked}</p>
                {n.promo_check?.original_price != null && (
                  <p>
                    Precio original: {n.promo_check.currency} {n.promo_check.original_price} ·
                    Promocional: {n.promo_check.currency} {n.promo_check.current_price}
                    {n.promo_check.savings != null ? ` · Ahorro: ${n.promo_check.currency} ${n.promo_check.savings}` : ""}
                  </p>
                )}
                <p className="opacity-80">
                  {n.status === "PROMO_DETECTED"
                    ? "La automatización fue detenida para este pedido. Haz la recarga manualmente y pulsa RECARGA REALIZADA."
                    : "No se envió ninguna operación al proveedor. Revisión manual requerida."}
                </p>
              </div>
            ))}
          </div>
        )}

        {!status?.enabled && (
          <p className="text-xs font-mono text-red-300 inline-flex items-center gap-2" data-testid="pa-stopped-note">
            <ShieldAlert className="w-4 h-4" />
            Automatización DETENIDA: no se ejecutan nuevas operaciones. Las ya enviadas sólo se conciliarán.
          </p>
        )}
      </div>
      <section data-testid="ff-nickname-provider-settings" className="rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-4">
        <p className="text-xs font-black uppercase tracking-wider text-cyan-200">Verificación de nickname Free Fire</p>
        <p className="mt-1 text-[11px] text-zinc-400">Sólo consulta el nombre del jugador. No modifica rutas de recarga ni automatización.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[['freefirecommunity','FreeFireCommunity · principal'],['smileone','Smile.one · respaldo manual']].map(([id,label]) => <button key={id} type="button" onClick={() => setNicknameVerifier(id)} data-testid={`ff-nickname-provider-${id}`} className={`border px-3 py-2 text-xs font-bold ${nicknameProvider === id ? "border-cyan-300 bg-cyan-300 text-[#061018]" : "border-zinc-600 text-zinc-300 hover:border-cyan-400"}`}>{label}</button>)}
        </div>
      </section>

      {/* COLA */}
      <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--ag7-border-soft)] flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-300" />
          <h4 className="font-heading font-extrabold text-white text-sm uppercase tracking-wide">Operaciones pendientes</h4>
          <span className="ml-auto text-[11px] font-mono text-[var(--ag7-text-muted)]">{pending.length} en cola</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="pa-pending-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)] border-b border-[var(--ag7-border-soft)]">
                {["Pedido", "Usuario", "Juego", "Producto", "UID", "Costo", "Fecha", "Estado", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-xs text-zinc-500" data-testid="pa-pending-empty">
                  No hay operaciones esperando ejecución.
                </td></tr>
              )}
              {pending.map((op) => (
                <Row key={op.operation_id} op={op} actions={
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openDetails(op)} data-testid={`pa-details-${op.operation_id}`}
                      className="px-2 py-1.5 rounded-lg border border-[var(--ag7-border)] text-zinc-300 text-[11px] font-bold inline-flex items-center gap-1 hover:bg-ag7-surface-2">
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </button>
                    <button
                      onClick={() => setConfirmOp(op)}
                      disabled={!status?.enabled || NON_EXECUTABLE.has(op.status) || busyId === op.operation_id}
                      data-testid={`pa-execute-${op.operation_id}`}
                      title={!status?.enabled ? "Automatización detenida" : "Ejecutar recarga"}
                      className="px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-black inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                      <Rocket className="w-3.5 h-3.5" /> Ejecutar
                    </button>
                  </div>
                } />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* HISTORIAL */}
      <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--ag7-border-soft)] flex flex-wrap items-center gap-3">
          <h4 className="font-heading font-extrabold text-white text-sm uppercase tracking-wide">Historial de automatizaciones</h4>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pa-history-search"
                placeholder="Pedido, usuario, UID, control…"
                className="pl-8 pr-3 py-1.5 rounded-xl bg-ag7-surface-2 border border-[var(--ag7-border)] text-xs text-white placeholder:text-zinc-500 w-56" />
            </div>
            <select value={historyStatus} onChange={(e) => setHistoryStatus(e.target.value)} data-testid="pa-history-status"
              className="px-2 py-1.5 rounded-xl bg-ag7-surface-2 border border-[var(--ag7-border)] text-xs text-white">
              <option value="">Todos</option>
              <option value="CONFIRMED">🟢 Confirmadas</option>
              <option value="PROMO_DETECTED">🎁 Promoción detectada</option>
              <option value="PROMO_CHECK_UNKNOWN">🟡 Promo sin verificar</option>
              <option value="NOT_ELIGIBLE">⏸ Manual / no elegible</option>
              <option value="SIMULATED">🧪 Simuladas</option>
              <option value="UNKNOWN">🟠 Indeterminadas</option>
              <option value="MANUAL_REVIEW">👤 Revisión humana</option>
              <option value="FAILED">🔴 Fallidas</option>
              <option value="BLOCKED">⛔ Bloqueadas</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="pa-history-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--ag7-text-muted)] border-b border-[var(--ag7-border-soft)]">
                {["Pedido", "Usuario", "Juego", "Producto", "UID", "Costo", "Fecha", "Estado", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-xs text-zinc-500" data-testid="pa-history-empty">
                  Sin automatizaciones registradas todavía.
                </td></tr>
              )}
              {history.map((op) => (
                <Row key={op.operation_id} op={op} actions={
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openDetails(op)} data-testid={`pa-details-${op.operation_id}`}
                      className="px-2 py-1.5 rounded-lg border border-[var(--ag7-border)] text-zinc-300 text-[11px] font-bold inline-flex items-center gap-1 hover:bg-ag7-surface-2">
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </button>
                    {(op.status === "BLOCKED" || op.status === "FAILED") && !op.post_sent && (
                      <button onClick={() => retry(op)} disabled={busyId === op.operation_id}
                        data-testid={`pa-retry-${op.operation_id}`}
                        title="El intento anterior no envió nada al proveedor: se puede reintentar sin riesgo de cobro doble"
                        className="px-2 py-1.5 rounded-lg border border-amber-400/50 text-amber-300 text-[11px] font-bold inline-flex items-center gap-1 hover:bg-amber-400/10 disabled:opacity-40">
                        <RefreshCw className={`w-3.5 h-3.5 ${busyId === op.operation_id ? "animate-spin" : ""}`} /> Reintentar
                      </button>
                    )}
                    {op.status === "NOT_ELIGIBLE" && (
                      <button onClick={() => reevaluate(op)} disabled={busyId === op.operation_id}
                        data-testid={`pa-reevaluate-${op.operation_id}`}
                        title="Recalcular elegibilidad con el catálogo y los límites actuales"
                        className="px-2 py-1.5 rounded-lg border border-ag7-cyan/50 text-ag7-cyan text-[11px] font-bold inline-flex items-center gap-1 hover:bg-ag7-cyan/10 disabled:opacity-40">
                        <RefreshCw className={`w-3.5 h-3.5 ${busyId === op.operation_id ? "animate-spin" : ""}`} /> Reevaluar
                      </button>
                    )}
                  </div>
                } />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PaConfirmModal operation={confirmOp} busy={!!busyId}
        onCancel={() => setConfirmOp(null)} onConfirm={execute} />
      <PaDetailsModal operation={detailsOp} onClose={() => setDetailsOp(null)} />
    </div>
  );
};
